import * as React from 'react';

import type { UppyEventMap } from '@uppy/core';
import { values } from 'lodash';

import type { PluginSourcesType } from 'bundles/asset-admin/constants/AssetUpload';
import type { UploadStatusType } from 'bundles/asset-admin/constants/UploadStatus';
import UploadStatus from 'bundles/asset-admin/constants/UploadStatus';
import type {
  Locale,
  OnBeforeUpload,
  TBody,
  TMeta,
  UploadCompletion,
  UppyEvents,
  UppyFiles,
  UppyProps,
  UppyRef,
  UppyWithMeta,
  onBeforeFileAdded,
} from 'bundles/asset-admin/types/uppy';
import AssetAdminTracking from 'bundles/asset-admin/utils/AssetAdminTracking';
import { mapAssetTypeToMimes } from 'bundles/asset-admin/utils/AssetUploaderUtils';
import createUppyInstance, { DEFAULT_KEY, DEFAULT_TEMPLATE_ID } from 'bundles/asset-admin/utils/UppyUtils';
import { getUppyLocalePackWithOverrides } from 'bundles/asset-admin/utils/uppyLocales';
import type { Assembly, TransloaditFile, TransloaditFileMap } from 'bundles/authoring/common/types/Upload';
import type { OptionsContext } from 'bundles/cml';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/asset-admin';

// eslint-disable-next-line no-restricted-syntax, import/extensions
import 'css!@uppy/core/dist/style.min.css';
import 'css!@uppy/dashboard/dist/style.min.css';

const MAX_CONCURRENT_UPLOADS = 100;

export type RenderProps = {
  uppy: UppyWithMeta;
  status: UploadStatusType;
};
type OptionalError = Error | undefined | null;

export type PropsToUppyAssetUploader = {
  courseId?: string;
  assetContext?: OptionsContext;
  assetCreationUrl?: string;
  templateId?: string;
  uploadToAssetService?: boolean;
  uploadPaused?: boolean;
  // If allowedFileTypes and assetType are both specified, allowedFileTypes will take precedence.
  allowedFileTypes?: Array<string>;
  assetType?: string;
  pdfTemplate?: string;
  allowMultiple?: boolean;
  uppyProps?: UppyProps;
  uppyRef?: UppyRef;
  transloaditPluginFields?: Record<string, string>;
  pluginSources?: Array<PluginSourcesType> | null;
  locale?: Partial<Locale>;
  showPostAssemblyLoader?: boolean;
};

/**
 * @deprecated
 * unless necessary, move ot using uppy.ts types
 */
export type UppyEventHandlerProps = {
  onFileAdded?: onBeforeFileAdded;
  onFilesReadyForUpload?: (files: UppyFiles, uppy: UppyWithMeta) => void;
  onUploadStart?: (files: UppyFiles) => void;
  onUploadProgress?: UppyEvents['upload-progress'];
  onUploadError: (assembly: Assembly, error: OptionalError) => void;
  onUploadSuccess?: (assembly: Assembly) => void;
  onUploadsComplete?: UppyEvents['complete'];
  onAssetCreationError: UppyEvents['assetCreation:error'];
  onAssetCreationComplete: (assetIds: Array<string>, assembly: Assembly) => void | Promise<void>;
  onFileUploadComplete?: UppyEvents['transloadit:complete'];
};

type State = RenderProps;

declare global {
  interface Window {
    uppyInstance?: UppyWithMeta;
  }
}

export type Props = PropsToUppyAssetUploader & UppyEventHandlerProps & { render: (props: RenderProps) => JSX.Element };

export default class UppyAssetUploader extends React.Component<Props, State> {
  static defaultProps = {
    onFilesReadyForUpload: (files: TransloaditFileMap) => files,
    onUploadStart: (files: TransloaditFileMap) => files,
    onUploadProgress: (file: TransloaditFile) => file,
    onUploadError: (error: Error) => error,
    onUploadSuccess: (assembly: Assembly) => assembly,
    onUploadsComplete: (uploadCompletion: UploadCompletion) => uploadCompletion,
    onAssetCreationError: (error: Error) => error,
    onAssetCreationComplete: (assetIds: string[]) => assetIds,
    onFileUploadComplete: (assembly: Assembly) => assembly,
  };

  constructor(props: Props) {
    super(props);

    const {
      uppyRef,
      uppyProps,
      allowMultiple,
      allowedFileTypes,
      assetType,
      courseId,
      assetContext,
      assetCreationUrl,
      templateId,
      pdfTemplate,
      uploadToAssetService,
      onAssetCreationComplete,
      onAssetCreationError,
      showPostAssemblyLoader,
      transloaditPluginFields,
      pluginSources,
    } = props;
    const uppy = createUppyInstance({
      params: {
        ...uppyProps,
        restrictions: {
          maxNumberOfFiles: allowMultiple ? MAX_CONCURRENT_UPLOADS : 1,
          allowedFileTypes: allowedFileTypes || (assetType && mapAssetTypeToMimes(assetType)) || null,
          maxFileSize: null,
          minFileSize: null,
          maxTotalFileSize: null,
          minNumberOfFiles: null,
          requiredMetaFields: [],
        },
        onBeforeUpload: this.handleFilesReadyForUpload,
        onBeforeFileAdded: this.handleFileAdded,
      },
      events: {
        'upload-progress': this.handleUploadProgress,
        'transloadit:assembly-error': this.handleAssemblyError,
        'transloadit:complete': this.handleFileUploadComplete,
        'assetCreation:complete': onAssetCreationComplete,
        'assetCreation:error': onAssetCreationError,
        complete: this.handleUploadsComplete,
      },
      transloaditPluginFields,
      key: DEFAULT_KEY,
      templateId: templateId ?? DEFAULT_TEMPLATE_ID,
      uppyId: `uppyInstance-${Date.now()}-${Math.random()}`,
      assetContext: assetContext || { courseId },
      assetCreationUrl,
      pdfTemplate,
      uploadToAssetService,
      showPostAssemblyLoader,
      pluginSources,
    });

    if (uppyRef) {
      uppyRef.current = uppy;
    }

    this.state = {
      uppy,
      status: UploadStatus.IDLE,
    };

    // used to interact with the uppy instance during e2e tests
    if (!inServerContext) {
      window.uppyInstance = uppy;
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { uppy } = this.state;
    const { uploadPaused } = this.props;

    if (prevProps.uploadPaused !== uploadPaused) {
      if (uploadPaused) {
        uppy.pauseAll();
      } else {
        uppy.resumeAll();
      }
    }
  }

  componentWillUnmount() {
    const { uppyProps } = this.props;
    const { uppy } = this.state;
    const { events = {} } = uppyProps || {};

    Object.entries({
      ...events,
      'transloadit:assembly-error': this.handleAssemblyError,
      'transloadit:complete': this.handleFileUploadComplete,
      complete: this.handleUploadsComplete,
    }).forEach(([event, action]) =>
      uppy.off(
        event as keyof UppyEventMap<TMeta, TBody>,
        action as UppyEventMap<TMeta, TBody>[keyof UppyEventMap<TMeta, TBody>]
      )
    );
  }

  handleAssemblyError = (assembly: Assembly, error: OptionalError): void => {
    const { onUploadError } = this.props;

    Promise.resolve(onUploadError(assembly, error)).then(() => this.setState({ status: UploadStatus.IDLE }));
  };

  handleFileAdded: onBeforeFileAdded = (file, files) => {
    const { onFileAdded } = this.props;

    return onFileAdded?.(file, files);
  };

  handleFilesReadyForUpload: OnBeforeUpload = (fileMap) => {
    const { onUploadStart, onFilesReadyForUpload } = this.props;
    const { uppy } = this.state;

    if (onFilesReadyForUpload) {
      onFilesReadyForUpload(fileMap, uppy);
    }

    this.setState({ status: UploadStatus.IN_PROGRESS }, () => {
      if (onUploadStart) {
        onUploadStart(fileMap);
      }
    });

    return true;
  };

  handleUploadProgress: UppyEvents['upload-progress'] = (file, progress): void => {
    const { onUploadProgress } = this.props;
    if (onUploadProgress) {
      onUploadProgress(file, progress);
    }
  };

  handleFailedUpload = (assembly: Assembly, error?: OptionalError) => {
    const { onUploadError } = this.props;

    const { uppy } = this.state;
    uppy.info(
      'There was an error in the upload process, please close this modal and try again or contact support',
      'error',
      0
    );

    const files = values(assembly.results)[0];
    const file = files[0];
    const assetType = file.meta.type;
    AssetAdminTracking.track('upload', 'failed', { assetType });

    onUploadError(assembly, error);
    this.setState({ status: UploadStatus.FAILED });
  };

  handleSuccessfulUpload = (assembly: Assembly) => {
    const { onUploadSuccess } = this.props;

    if (onUploadSuccess) {
      onUploadSuccess(assembly);
    }
    this.setState({ status: UploadStatus.IDLE });
  };

  handleFileUploadComplete: UppyEvents['transloadit:complete'] = (assembly) => {
    const { onFileUploadComplete } = this.props;

    if (assembly.error) {
      this.handleFailedUpload(assembly);
    } else {
      this.handleSuccessfulUpload(assembly);
    }

    // Each uppy UI will behave differently after upload, in terms of choosing whether
    // to reset uppy state. Users of this component must implement that in their onUploadError,
    // onAssetCreationError, and onAssetCreationComplete callbacks if reset logic is needed.
    // See `bundles/asset-admin/components/DragDropUploader.jsx` for an example.
    if (onFileUploadComplete) {
      onFileUploadComplete(assembly);
    }
  };

  handleUploadsComplete: UppyEvents['complete'] = (uploadsCompletion) => {
    const { onUploadsComplete } = this.props;
    if (onUploadsComplete) {
      onUploadsComplete(uploadsCompletion);
    }
  };

  setLocalePack() {
    const { uppyRef } = this.props;
    const localePack = this.getLocalePack();
    if (uppyRef?.current) {
      uppyRef.current.setOptions({ locale: localePack });
    }
  }

  getLocalePack = (): Locale => {
    const { locale, allowMultiple } = this.props;
    const overrides: Partial<Locale> = {
      strings: {
        done: _t('Start over'),
        cancel: _t('Clear'),
        authenticateWithTitle: _t('Authenticate with %{pluginName} to select files'),
        ...(allowMultiple === false
          ? {
              dropPasteImport: _t('Drop a file here or import from:'),
              dropPasteFiles: _t('Drop a file here or import from:'),
              dropPasteFolders: _t('Drop a file here or import from:'),
              dropPasteBoth: _t('Drop a file here or import from:'),
              dropPasteImportFiles: _t('Drop a file here or import from:'),
              dropPasteImportFolders: _t('Drop a file here or import from:'),
              dropPasteImportBoth: _t('Drop a file here or import from:'),
            }
          : {}),
        ...locale?.strings,
      },
    };
    return getUppyLocalePackWithOverrides(_t.getLocale(), overrides);
  };

  render() {
    const { render } = this.props;

    // we do this here in the render method for CSR / SSR consistency for the accessed user locale, and force an update in uppy
    if (this.props.uppyRef?.current) {
      this.setLocalePack();
    }

    return this.state.uppy ? render(this.state) : null;
  }
}
